import { useState, useEffect, useCallback, useMemo } from "react";
import { getPassengerSpecialTasks } from "../api/tasks.api";
import type { SpecialTask } from "../api/tasks.api";
import { ApiError } from "../services/api.service";

/**
 * Hook to fetch special tasks for multiple passengers
 */
export function usePassengersSpecialTasks(passengerIds: number[]) {
	const [specialTasksMap, setSpecialTasksMap] = useState<Record<number, SpecialTask[]>>({});
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	// Memoize the passenger IDs to prevent infinite re-renders
	const stablePassengerIds = useMemo(() => {
		// Create a stable array by sorting and stringifying
		const sorted = [...passengerIds].sort((a, b) => a - b);
		return sorted;
	}, [passengerIds.join(',')]);

	const fetchSpecialTasks = useCallback(async () => {
		if (!stablePassengerIds.length) {
			setSpecialTasksMap({});
			return;
		}

		try {
			setIsLoading(true);
			setError(null);

			// Fetch special tasks for all passengers in parallel
			const promises = stablePassengerIds.map(async (passengerId) => {
				try {
					const response = await getPassengerSpecialTasks(passengerId);
					return { passengerId, specialTasks: response.specialTasks };
				} catch (err) {
					// If individual passenger fails, return empty array
					console.warn(`Failed to fetch special tasks for passenger ${passengerId}:`, err);
					return { passengerId, specialTasks: [] };
				}
			});

			const results = await Promise.all(promises);

			// Convert to map
			const map: Record<number, SpecialTask[]> = {};
			results.forEach(({ passengerId, specialTasks }) => {
				map[passengerId] = specialTasks;
			});

			setSpecialTasksMap(map);
		} catch (err) {
			const errorMessage =
				err instanceof ApiError
					? err.message
					: "Fehler beim Laden der besonderen Anforderungen";
			setError(errorMessage);
			setSpecialTasksMap({});
		} finally {
			setIsLoading(false);
		}
	}, [stablePassengerIds]);

	useEffect(() => {
		fetchSpecialTasks();
	}, [fetchSpecialTasks]);

	return {
		specialTasksMap,
		isLoading,
		error,
		refetch: fetchSpecialTasks,
	};
}

/**
 * Hook to fetch special tasks for a single passenger (legacy compatibility)
 */
export function usePassengerSpecialTasks(passengerId: number | null) {
	const passengerIds = passengerId ? [passengerId] : [];
	const { specialTasksMap, isLoading, error, refetch } = usePassengersSpecialTasks(passengerIds);

	return {
		data: passengerId ? specialTasksMap[passengerId] || [] : [],
		isLoading,
		error,
		refetch,
	};
}